import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Music2, ScrollText } from "lucide-react";
import { PageHero, DraftNotice, SectionEyebrow } from "@/components/common/SectionHeading";
import SEO from "@/components/common/SEO";
import { fetchStatements, fetchLeadership } from "@/lib/lnlApi";

function StatementCard({ s }) {
    const isPoem = s.code === "hymn";
    return (
        <div className="lnl-card p-6" data-testid={`statement-${s.code}`}>
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                    {s.code === "hymn" && <Music2 className="h-5 w-5 text-lnl-gold" />}
                    <h2 className="font-heading text-2xl font-bold text-lnl-charcoal">{s.title}</h2>
                </div>
                {s.status !== "approved" && (
                    <span className="text-[10px] uppercase tracking-widest font-heading font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-0.5">
                        {s.status.replace("_", " ")}
                    </span>
                )}
            </div>
            <span className="lnl-gold-rule mt-3 block" />
            {isPoem ? (
                <p className="mt-4 font-serif-accent text-[15px] text-lnl-charcoal whitespace-pre-line leading-[1.85]">
                    {s.body}
                </p>
            ) : (
                <p className="mt-4 text-lnl-charcoal whitespace-pre-line leading-relaxed">{s.body}</p>
            )}
            {s.notice && <div className="mt-4"><DraftNotice tone="warn">{s.notice}</DraftNotice></div>}
        </div>
    );
}

export default function About() {
    const [statements, setStatements] = useState([]);
    const [leaders, setLeaders] = useState([]);
    useEffect(() => {
        fetchStatements().then(setStatements);
        fetchLeadership().then(setLeaders);
    }, []);

    const byCode = Object.fromEntries(statements.map((s) => [s.code, s]));
    const has = (code) => byCode[code] && (byCode[code].body?.trim().length > 0 || byCode[code].notice);

    return (
        <div data-testid="about-page">
            <SEO
                title="About LNL — Vision, Mission & Leadership"
                description="Learn about Lyceum Northern Luzon (LNL) — vision, mission, philosophy, institutional outcomes, hymn and leadership in Urdaneta City, Pangasinan."
                path="/about"
            />
            <PageHero
                eyebrow="About LNL"
                title="About Lyceum Northern Luzon"
                description="An established private higher-education institution in Urdaneta City serving learners across Pangasinan and Northern Luzon."
                breadcrumbs={[{ label: "Home", to: "/" }, { label: "About LNL" }]}
            />

            <section className="py-10 bg-white">
                <div className="lnl-container grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
                    <div className="lg:col-span-2 space-y-5">
                        {byCode.vision && <StatementCard s={byCode.vision} />}
                        {byCode.mission && <StatementCard s={byCode.mission} />}
                        {byCode.philosophy && <StatementCard s={byCode.philosophy} />}
                        {byCode.outcomes && <StatementCard s={byCode.outcomes} />}
                        {byCode.hymn && <StatementCard s={byCode.hymn} />}

                        <div className="lnl-card p-6" data-testid="statement-history">
                            <div className="flex items-center gap-2">
                                <ScrollText className="h-5 w-5 text-lnl-gold" />
                                <h2 className="font-heading text-2xl font-bold text-lnl-charcoal">A Brief History</h2>
                            </div>
                            <span className="lnl-gold-rule mt-3 block" />
                            <DraftNotice tone="warn">
                                The official institutional history has not yet been provided. This section is a placeholder — the approved narrative will replace it once the institution supplies the wording.
                            </DraftNotice>
                            <p className="mt-3 text-lnl-gray tracking-widest">.....................</p>
                        </div>
                    </div>

                    <aside className="lg:col-span-1 h-fit space-y-4">
                        <div className="lnl-card p-5" data-testid="board-of-trustees-side">
                            <SectionEyebrow>Governance</SectionEyebrow>
                            <div className="font-heading font-bold text-xl text-lnl-charcoal mt-1">Board of Trustees</div>
                            <p className="text-sm text-lnl-gray mt-2">
                                The Board of Trustees leads the direction of Lyceum Northern Luzon and its academic community.
                            </p>
                            <ul className="mt-4 space-y-2 text-sm">
                                {leaders.map((l) => (
                                    <li key={l.id} className="border-b border-lnl-border last:border-0 pb-2 last:pb-0">
                                        <div className="text-[10px] uppercase tracking-widest font-heading font-semibold text-lnl-green-deep">{l.title}</div>
                                        <div className="text-lnl-charcoal font-heading font-semibold">{l.name}</div>
                                    </li>
                                ))}
                            </ul>
                            <Link to="/about/leadership" className="lnl-link mt-4 inline-block text-sm">See full leadership roster</Link>
                        </div>
                    </aside>
                </div>
            </section>

            <section className="py-14 bg-lnl-off-white border-t border-lnl-border" data-testid="board-of-trustees">
                <div className="lnl-container">
                    <SectionEyebrow>Board of Trustees</SectionEyebrow>
                    <h2 className="font-heading text-3xl sm:text-4xl font-bold text-lnl-charcoal mt-2">The people leading LNL</h2>
                    <span className="lnl-gold-rule mt-3 block" />
                    <div className="mt-6"><DraftNotice tone="warn">Every listed record is seeded from currently published information and marked "Verification Required Before Launch". Personal telephone numbers and private emails are never displayed.</DraftNotice></div>
                    <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {leaders.map((l) => (
                            <div key={l.id} className="lnl-card p-5 bg-white" data-testid={`bot-${l.order}`}>
                                <div className="text-[10px] uppercase tracking-[0.18em] font-heading font-semibold text-lnl-green-deep">{l.title}</div>
                                <div className="mt-2 flex items-center gap-3">
                                    <div className="w-12 h-12 rounded-full bg-lnl-green-bg text-lnl-green-deep font-heading font-bold grid place-items-center">
                                        {l.name.split(" ").map((x) => x[0]).join("").slice(0, 2)}
                                    </div>
                                    <div className="font-heading font-bold text-lnl-charcoal leading-tight">{l.name}</div>
                                </div>
                                <div className="mt-3 text-[10px] uppercase tracking-widest font-heading font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-0.5 inline-block">
                                    Verification required
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}
